import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, LogOut, Lock, FileText, ArrowLeft, Plus, Save, KeyRound, Download } from "lucide-react";
import { StaxLogo } from "@/components/stax/Logo";
import { fetchContent } from "@/lib/content";
import { ITEMS as CART_DEFAULTS } from "@/components/stax/BuildCart";
import { AnalyticsTab } from "./admin/AnalyticsTab";
import { LeadsTab } from "./admin/LeadsTab";
import { EmailsTab } from "./admin/EmailsTab";
import { LoyaltyTab } from "./admin/LoyaltyTab";
import { InvoicesTab } from "./admin/InvoicesTab";
import { CustomersTab } from "./admin/CustomersTab";
import { ChatTab } from "./admin/ChatTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FREEBIES = ["brand-checklist", "social-sizing", "brand-questionnaire", "launch-checklist"];
const PROJECTS = ["lumen", "pulse", "field", "rocket", "nova", "atlas", "nanas-obituary"];

const errMsg = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return "Something went wrong.";
};

const DEFAULT_CONTENT = {
  colors: { yellow: "#FFE600", magenta: "#EC008C", cyan: "#00AEEF" },
  hero: {
    lines: [
      { text: "Let Your" },
      { text: "Brand" },
      { text: "Speak", pink: true },
      { text: "For Itself." },
    ],
    sub: "STAX MARKETING LLC is a creative design & marketing studio helping businesses build brands that look as good as they perform.",
  },
  tiers: [150, 300, 550],
  cart_items: CART_DEFAULTS,
};

const cfield =
  "w-full border-2 border-ink bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-staxmagenta";

const TIER_NAMES = ["Stay Live", "Growth", "Elite Partner"];

const ContentEditor = ({ auth }) => {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchContent(true).then((c) => {
      setDraft({
        notify_email: c.notify_email || "",
        colors: { ...DEFAULT_CONTENT.colors, ...(c.colors || {}) },
        hero: {
          lines: c.hero?.lines?.length ? c.hero.lines : DEFAULT_CONTENT.hero.lines.map((l) => ({ ...l })),
          sub: c.hero?.sub || DEFAULT_CONTENT.hero.sub,
        },
        tiers: c.tiers?.length ? c.tiers : [...DEFAULT_CONTENT.tiers],
        cart_items: c.cart_items?.length ? c.cart_items : CART_DEFAULTS.map((i) => ({ ...i })),
        sections: {
          work: !!c.sections?.work,
          testimonials: !!c.sections?.testimonials,
          freebies: !!c.sections?.freebies,
        },
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!draft)
    return <p className="p-6 font-mono2 text-xs uppercase tracking-widest text-ink/50">Loading content...</p>;

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/content`, draft, auth);
      await fetchContent(true);
      toast.success("Saved — refresh the site to see it live.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-content-editor">
      <section className="border-4 border-ink bg-white text-ink shadow-hard-sm">
        <div className="border-b-4 border-ink bg-staxmagenta px-5 py-3">
          <h3 className="font-display uppercase text-lg text-white">Brand Colors</h3>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[["yellow", "Yellow"], ["magenta", "Pink"], ["cyan", "Cyan"]].map(([k, label]) => (
            <label key={k} data-testid={`color-${k}`} className="flex items-center gap-3">
              <input
                type="color"
                value={draft.colors[k]}
                onChange={(e) => setDraft((d) => ({ ...d, colors: { ...d.colors, [k]: e.target.value } }))}
                className="h-10 w-14 cursor-pointer border-2 border-ink bg-white"
              />
              <span className="font-mono2 text-xs font-bold uppercase tracking-widest">
                {label}
                <br />
                <span className="text-ink/40">{draft.colors[k]}</span>
              </span>
            </label>
          ))}
        </div>
      </section>

      <section className="border-4 border-ink bg-white text-ink shadow-hard-sm">
        <div className="border-b-4 border-ink bg-ink px-5 py-3">
          <h3 className="font-display uppercase text-lg text-white">Lead Notifications</h3>
        </div>
        <div className="p-5">
          <p className="mb-3 font-mono2 text-[10px] uppercase tracking-widest text-ink/45">
            Get an email the moment someone sends an inquiry. Leave blank to turn off.
          </p>
          <input
            data-testid="notify-email-input"
            type="email"
            className={cfield}
            value={draft.notify_email}
            onChange={(e) => setDraft((d) => ({ ...d, notify_email: e.target.value }))}
            placeholder="you@yourbusiness.com"
          />
        </div>
      </section>

      <section className="border-4 border-ink bg-white text-ink shadow-hard-sm">
        <div className="border-b-4 border-ink bg-staxyellow px-5 py-3">
          <h3 className="font-display uppercase text-lg text-ink">Section Visibility</h3>
        </div>
        <div className="p-5 space-y-3">
          <p className="font-mono2 text-[10px] uppercase tracking-widest text-ink/45">
            Hide sections you don't have content for yet. They disappear from the site and the menu until you switch them back on.
          </p>
          {[
            ["work", "The Work (Portfolio)"],
            ["testimonials", "Testimonials"],
            ["freebies", "Freebies (Creative Resources)"],
          ].map(([key, label]) => (
            <div key={key} data-testid={`section-toggle-${key}`} className="flex items-center justify-between border-2 border-ink/15 px-4 py-3">
              <span className="font-display uppercase text-sm">{label}</span>
              <span className="flex items-center gap-3">
                <span className={`font-mono2 text-[10px] font-bold uppercase tracking-[0.2em] ${draft.sections[key] ? "text-ink" : "text-ink/40"}`}>
                  {draft.sections[key] ? "Shown" : "Hidden"}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={draft.sections[key]}
                  data-testid={`section-toggle-${key}-switch`}
                  onClick={() => setDraft((d) => ({ ...d, sections: { ...d.sections, [key]: !d.sections[key] } }))}
                  className={`relative h-6 w-12 shrink-0 border-2 border-ink transition-colors ${draft.sections[key] ? "bg-staxyellow" : "bg-white"}`}
                >
                  <span className={`absolute top-0.5 h-4 w-4 bg-ink transition-all ${draft.sections[key] ? "left-[26px]" : "left-0.5"}`} />
                </button>
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="border-4 border-ink bg-white text-ink shadow-hard-sm">
        <div className="border-b-4 border-ink bg-staxcyan px-5 py-3">
          <h3 className="font-display uppercase text-lg">Hero Text</h3>
        </div>
        <div className="p-5 space-y-3">
          {draft.hero.lines.map((l, i) => (
            <div key={i} className="flex items-center gap-3">
              <input
                data-testid={`hero-line-${i}`}
                className={cfield}
                value={l.text}
                onChange={(e) =>
                  setDraft((d) => {
                    const lines = [...d.hero.lines];
                    lines[i] = { ...lines[i], text: e.target.value };
                    return { ...d, hero: { ...d.hero, lines } };
                  })
                }
              />
              <label className="flex items-center gap-1.5 font-mono2 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">
                <input
                  type="radio"
                  name="hero-pink"
                  data-testid={`hero-pink-${i}`}
                  checked={!!l.pink}
                  onChange={() =>
                    setDraft((d) => ({
                      ...d,
                      hero: { ...d.hero, lines: d.hero.lines.map((x, j) => ({ ...x, pink: j === i })) },
                    }))
                  }
                />
                Pink
              </label>
            </div>
          ))}
          <textarea
            data-testid="hero-sub"
            className={`${cfield} min-h-[70px] resize-none`}
            value={draft.hero.sub}
            onChange={(e) => setDraft((d) => ({ ...d, hero: { ...d.hero, sub: e.target.value } }))}
            placeholder="Hero subheadline"
          />
        </div>
      </section>

      <section className="border-4 border-ink bg-white text-ink shadow-hard-sm">
        <div className="border-b-4 border-ink bg-staxyellow px-5 py-3">
          <h3 className="font-display uppercase text-lg">Care Plan Prices ($/mo)</h3>
        </div>
        <div className="p-5 grid grid-cols-3 gap-4">
          {TIER_NAMES.map((n, i) => (
            <label key={n} className="block">
              <span className="font-mono2 text-[10px] font-bold uppercase tracking-widest text-ink/50">{n}</span>
              <input
                data-testid={`tier-price-${i}`}
                type="number"
                min="0"
                className={cfield}
                value={draft.tiers[i]}
                onChange={(e) =>
                  setDraft((d) => {
                    const tiers = [...d.tiers];
                    tiers[i] = parseInt(e.target.value) || 0;
                    return { ...d, tiers };
                  })
                }
              />
            </label>
          ))}
        </div>
      </section>

      <section className="border-4 border-ink bg-white text-ink shadow-hard-sm">
        <div className="border-b-4 border-ink bg-ink px-5 py-3 flex items-center justify-between">
          <h3 className="font-display uppercase text-lg text-white">Cart Items</h3>
          <button
            data-testid="cart-item-add"
            onClick={() =>
              setDraft((d) => ({
                ...d,
                cart_items: [...d.cart_items, { id: `custom-${Date.now()}`, name: "New Item", desc: "", price: null, color: "yellow" }],
              }))
            }
            className="flex items-center gap-1.5 border-2 border-staxyellow bg-staxyellow px-3 py-1.5 font-mono2 text-[10px] font-bold uppercase tracking-widest text-ink"
          >
            <Plus size={12} /> Add item
          </button>
        </div>
        <div className="p-5 space-y-4">
          {draft.cart_items.map((item, i) => (
            <div key={item.id} data-testid={`cart-row-${item.id}`} className="border-2 border-ink/15 p-4 space-y-2">
              <div className="flex gap-2">
                <input
                  className={cfield}
                  value={item.name}
                  placeholder="Item name"
                  onChange={(e) =>
                    setDraft((d) => {
                      const ci = [...d.cart_items];
                      ci[i] = { ...ci[i], name: e.target.value };
                      return { ...d, cart_items: ci };
                    })
                  }
                />
                <select
                  className="w-28 border-2 border-ink bg-white px-2 text-sm"
                  value={item.color}
                  onChange={(e) =>
                    setDraft((d) => {
                      const ci = [...d.cart_items];
                      ci[i] = { ...ci[i], color: e.target.value };
                      return { ...d, cart_items: ci };
                    })
                  }
                >
                  <option value="yellow">Yellow</option>
                  <option value="magenta">Pink</option>
                  <option value="cyan">Cyan</option>
                  <option value="ink">Black</option>
                </select>
                <button
                  data-testid={`cart-row-delete-${item.id}`}
                  onClick={() => setDraft((d) => ({ ...d, cart_items: d.cart_items.filter((_, j) => j !== i) }))}
                  aria-label="Delete item"
                  className="border-2 border-ink p-2 hover:bg-staxmagenta hover:text-white transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  className={cfield}
                  value={item.desc}
                  placeholder="Short description"
                  onChange={(e) =>
                    setDraft((d) => {
                      const ci = [...d.cart_items];
                      ci[i] = { ...ci[i], desc: e.target.value };
                      return { ...d, cart_items: ci };
                    })
                  }
                />
                <input
                  className="w-32 border-2 border-ink bg-white px-3 py-2 text-sm"
                  type="number"
                  min="0"
                  value={item.price ?? ""}
                  placeholder="Price $"
                  onChange={(e) =>
                    setDraft((d) => {
                      const ci = [...d.cart_items];
                      ci[i] = { ...ci[i], price: e.target.value === "" ? null : parseInt(e.target.value) || 0 };
                      return { ...d, cart_items: ci };
                    })
                  }
                />
              </div>
              <p className="font-mono2 text-[9px] uppercase tracking-widest text-ink/35">Empty price = "Custom quote"</p>
            </div>
          ))}
        </div>
      </section>

      <button
        data-testid="admin-save-content"
        onClick={save}
        disabled={busy}
        className="flex w-full items-center justify-center gap-2 border-2 border-ink bg-staxmagenta px-6 py-4 font-display uppercase text-lg text-white shadow-hard-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60"
      >
        <Save size={18} /> {busy ? "Saving..." : "Save All Changes"}
      </button>
      <p className="text-center font-mono2 text-[10px] uppercase tracking-widest text-white/40">
        Changes go live immediately — refresh the site to see them.
      </p>
    </div>
  );
};


const AccountCard = ({ auth }) => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const field =
    "w-full border-2 border-ink bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-staxmagenta";

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: cur, new_password: next }, auth);
      toast.success("Password updated — use it next time you log in.");
      setCur(""); setNext("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="account-card" className="mt-8 border-4 border-ink bg-white text-ink shadow-hard-sm">
      <div className="border-b-4 border-ink bg-ink px-5 py-3 flex items-center gap-2">
        <KeyRound size={16} className="text-staxyellow" />
        <h3 className="font-display uppercase text-lg text-white">Admin Password</h3>
      </div>
      <form onSubmit={submit} className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          data-testid="current-password"
          type="password"
          value={cur}
          onChange={(e) => setCur(e.target.value)}
          placeholder="Current password"
          className={field}
        />
        <input
          data-testid="new-password"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder="New password (8+ chars)"
          className={field}
        />
        <button data-testid="change-password-button" type="submit" disabled={busy}
          className="border-2 border-ink bg-ink px-5 py-3 font-display uppercase text-sm text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50">
          {busy ? "Updating..." : "Update Password"}
        </button>
      </form>
    </section>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("stax_admin_token") || "");
  const [pw, setPw] = useState("");
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("freebie");
  const [refId, setRefId] = useState(FREEBIES[0]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("analytics");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/files`, auth);
      setFiles(r.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("stax_admin_token");
        setToken("");
      }
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem("stax_admin_token", r.data.token);
      setToken(r.data.token);
      toast.success("Welcome back.");
    } catch {
      toast.error("Wrong password.");
    } finally {
      setBusy(false);
    }
  };

  const doUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Pick a file first.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("ref_id", refId);
      fd.append("file", file);
      await axios.post(`${API}/admin/upload`, fd, auth);
      toast.success("Uploaded and live.");
      setFile(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    try {
      await axios.post(`${API}/admin/files/${id}/delete`, {}, auth);
      toast.success("Removed.");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const logout = () => {
    localStorage.removeItem("stax_admin_token");
    setToken("");
  };

  const field =
    "w-full border-2 border-ink bg-white px-4 py-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-staxmagenta";
  const refs = category === "freebie" ? FREEBIES : category === "portfolio" ? PROJECTS : [];

  if (!token) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-ink flex items-center justify-center px-4">
        <form onSubmit={login} className="w-full max-w-sm border-4 border-ink bg-paper shadow-hard-yellow">
          <div className="border-b-4 border-ink bg-staxyellow px-6 py-4 flex items-center gap-3">
            <Lock size={18} />
            <span className="font-display uppercase text-xl">STAX Admin</span>
          </div>
          <div className="p-6 space-y-4">
            <input
              data-testid="admin-login-password"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Admin password"
              className={field}
            />
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={busy}
              className="w-full border-2 border-ink bg-staxmagenta px-5 py-3.5 font-display uppercase text-white shadow-hard-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              {busy ? "Checking..." : "Log In"}
            </button>
            <a href="/" data-testid="admin-back-link" className="flex items-center justify-center gap-1.5 font-mono2 text-xs uppercase tracking-widest text-ink/50 hover:text-staxmagenta">
              <ArrowLeft size={13} /> Back to site
            </a>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink text-white">
      <header className="border-b-4 border-white/15">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 sm:px-8 py-5">
          <StaxLogo dark size={38} />
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-back-link" className="font-mono2 text-xs uppercase tracking-widest text-white/50 hover:text-staxyellow">
              View site
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 border-2 border-white/40 px-3 py-2 font-mono2 text-xs uppercase tracking-widest hover:bg-staxmagenta hover:border-staxmagenta transition-colors"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 sm:px-8 pt-8 flex flex-wrap gap-3">
        {[
          ["analytics", "Analytics"],
          ["leads", "Leads"],
          ["chat", "Chat"],
          ["customers", "Customers"],
          ["invoices", "Invoices"],
          ["emails", "Emails"],
          ["loyalty", "Loyalty"],
          ["files", "Files & Media"],
          ["content", "Edit Content"],
        ].map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`border-2 px-5 py-2.5 font-display uppercase text-sm transition-colors ${
              tab === id ? "border-ink bg-staxyellow text-ink" : "border-white/30 bg-transparent text-white/60 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <main className="mx-auto max-w-5xl px-4 sm:px-8 py-10">
        {tab === "analytics" && <AnalyticsTab auth={auth} />}
        {tab === "leads" && <LeadsTab auth={auth} />}
        {tab === "chat" && <ChatTab auth={auth} />}
        {tab === "customers" && <CustomersTab auth={auth} />}
        {tab === "invoices" && <InvoicesTab auth={auth} />}
        {tab === "emails" && <EmailsTab auth={auth} />}
        {tab === "loyalty" && <LoyaltyTab auth={auth} />}
        {tab === "content" && (
          <>
            <ContentEditor auth={auth} />
            <AccountCard auth={auth} />
          </>
        )}
        {tab === "files" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="border-4 border-white/90 bg-paper text-ink shadow-hard-yellow">
          <div className="border-b-4 border-ink bg-staxcyan px-6 py-4">
            <h2 className="font-display uppercase text-2xl">Upload a File</h2>
          </div>
          <form onSubmit={doUpload} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <select
                data-testid="admin-upload-category"
                value={category}
                onChange={(e) => {
                  const c = e.target.value;
                  setCategory(c);
                  setRefId(c === "freebie" ? FREEBIES[0] : c === "portfolio" ? PROJECTS[0] : "");
                }}
                className={field}
              >
                <option value="freebie">Freebie resource</option>
                <option value="portfolio">Portfolio image</option>
                <option value="misc">Misc</option>
              </select>
              {refs.length > 0 && (
                <select data-testid="admin-upload-ref" value={refId} onChange={(e) => setRefId(e.target.value)} className={field}>
                  {refs.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              )}
            </div>
            <label
              data-testid="admin-upload-file-label"
              className="flex cursor-pointer items-center gap-3 border-2 border-dashed border-ink/40 bg-white px-4 py-6 text-sm font-medium text-ink/60 hover:border-staxmagenta"
            >
              <Upload size={18} className="text-staxmagenta" />
              <span className="truncate">{file ? file.name : "Choose file — PDF, image, doc, zip (25MB max)"}</span>
              <input
                data-testid="admin-upload-file"
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.txt,.zip,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
            <button
              data-testid="admin-upload-button"
              type="submit"
              disabled={busy}
              className="w-full border-2 border-ink bg-staxmagenta px-5 py-3.5 font-display uppercase text-white shadow-hard-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              {busy ? "Uploading..." : "Upload"}
            </button>
            <p className="font-mono2 text-[10px] uppercase tracking-widest text-ink/40">
              Uploading to the same slot replaces the previous file.
            </p>
          </form>
        </section>

        <section className="border-4 border-white/90 bg-paper text-ink shadow-hard-yellow">
          <div className="border-b-4 border-ink bg-staxyellow px-6 py-4">
            <h2 className="font-display uppercase text-2xl">Live Files</h2>
          </div>
          <div data-testid="admin-file-list" data-lenis-prevent className="max-h-[420px] overflow-y-auto p-4 space-y-2">
            {files.length === 0 && (
              <p className="p-4 font-mono2 text-xs uppercase tracking-widest text-ink/40">No files yet.</p>
            )}
            {files.map((f) => (
              <div key={f.id} data-testid={`admin-file-${f.id}`} className="flex items-center gap-3 border-2 border-ink/15 bg-white px-4 py-3">
                <FileText size={16} className="shrink-0 text-staxcyan" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{f.original_filename}</p>
                  <p className="font-mono2 text-[10px] uppercase tracking-widest text-ink/45">
                    {f.category}{f.ref_id ? ` / ${f.ref_id}` : ""} · {(f.size / 1024).toFixed(0)} KB
                  </p>
                </div>
                <a
                  data-testid={`admin-file-download-${f.id}`}
                  href={`${API}/files/${f.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open or download file"
                  className="border-2 border-ink p-1.5 hover:bg-staxcyan transition-colors"
                >
                  <Download size={14} />
                </a>
                <button
                  data-testid={`admin-file-delete-${f.id}`}
                  onClick={() => del(f.id)}
                  aria-label="Delete file"
                  className="border-2 border-ink p-1.5 hover:bg-staxmagenta hover:text-white transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>
          </div>
        )}
      </main>
    </div>
  );
}
